import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    parseShadowOrgSlug,
    prefixTenantPath,
    shadowPrefixForSlug,
    parseClubAppPrefix,
    stripShadowPrefix,
} from '../lib/tenantPath';
import { headerHeightFor } from '../lib/tenantChrome';

interface TenantConfig {
    id: string;
    name: string;
    slug: string;
    subdomain?: string;
    custom_domain?: string;
    logoUrl?: string;
    branding: any;
    hero?: {
        headline?: string;
        subheadline?: string;
        description?: string;
        ctaButtons?: Array<{ text: string; link: string; style?: string }>;
    };
    landingFeatures?: { icon: string; title: string; description: string; link?: string }[] | null;
    landingSlides?: { title: string; label?: string; date?: string; location?: string; description?: string; link?: string; imageUrl?: string }[] | null;
    landingStyle?: string | null;
    auth?: {
        methods?: string[] | null;
        allowedEmailDomains?: string[] | null;
        domainRejectionMessage?: string | null;
    } | null;
    seasonTenant?: Record<string, unknown> | null;
    /** Which tabs /player/home shows for this club. */
    playerHome?: {
        /** Tab ids to keep out of the tab bar — e.g. a club that takes no dues hides 'billing'. */
        hiddenTabs?: string[] | null;
        /** Show the Practice tab of training tools. */
        practiceTab?: boolean;
    } | null;
    landingCustomStyle?: {
        layout?: 'split' | 'centered';
        bgType?: 'solid' | 'gradient' | 'dark';
        bgColor?: string;
        bgColor2?: string;
        textColor?: 'light' | 'dark';
        accentColor?: string;
        fontStyle?: 'sans' | 'serif' | 'mono';
        corners?: 'sharp' | 'rounded' | 'pill';
        shadow?: 'none' | 'soft' | 'hard';
    } | null;
    features: any;
    plan: string;
    customNavLinks?: { label: string; url: string }[];
    /** Published org pages shown in tenant header (order = navigation_order) */
    websiteNavItems?: { label: string; href: string; external: boolean; pageType?: string | null }[];
    /** The club's own events page, if it has one — where "Events" points. */
    eventsPageHref?: string | null;
    /**
     * Members-only tenant app: inside `scopePrefix` (the whole app when null),
     * every path outside `publicPaths` needs a signed-in player, and the app
     * root lands on `homePath`. Enforced by `TenantAccessGate`.
     */
    access?: {
        requireSignIn?: boolean;
        scopePrefix?: string | null;
        homePath?: string | null;
        publicPaths?: string[] | null;
    } | null;
    /** Organization default UI language (same codes as LanguageContext). */
    defaultDisplayLanguage?: string | null;
    /** Open hours banner and other club-site chrome */
    clubSite?: { openHoursBanner?: string; openHoursDetail?: string };
}

interface TenantContextType {
    tenant: TenantConfig | null;
    loading: boolean;
    error: string | null;
    /** True when hostname tenant or /org/:slug shadow tenant is active */
    isTenant: boolean;
    /** True when browsing under /org/:slug on the main platform domain */
    isShadowTenant: boolean;
    /** e.g. /org/mm when shadow tenant is active */
    shadowPrefix: string | null;
    /** The club's colour, or null when it has not set one. */
    accent: string | null;
    /**
     * True when this club opts into the editorial styling
     * (`branding.theme === 'editorial'`). The platform and every other tenant
     * keep the default look.
     */
    editorial: boolean;
    /** Prefix in-app paths for the current tenant context */
    tenantPath: (path: string) => string;
    /** Strip /org/:slug prefix for route matching */
    stripTenantPrefix: (pathname: string) => string;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const [hostnameTenant, setHostnameTenant] = useState<TenantConfig | null>(null);
    const [shadowTenant, setShadowTenant] = useState<TenantConfig | null>(null);
    const [hostnameLoading, setHostnameLoading] = useState(true);
    const [shadowLoading, setShadowLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const shadowSlug = useMemo(
        () => (hostnameTenant ? null : parseShadowOrgSlug(location.pathname)),
        [location.pathname, hostnameTenant]
    );
    /**
     * On a host whose root is the club's own static site, the tenant app lives
     * under /events, so every in-app link needs that prefix — the same
     * mechanism the /org/:slug shadow sites use.
     */
    const clubAppPrefix = useMemo(() => parseClubAppPrefix(location.pathname), [location.pathname]);
    const shadowPrefix = shadowSlug ? shadowPrefixForSlug(shadowSlug) : clubAppPrefix;
    const tenant = hostnameTenant || shadowTenant;
    const loading = hostnameLoading || (!!shadowSlug && shadowLoading && !shadowTenant);

    useEffect(() => {
        let cancelled = false;
        const fetchHostnameTenant = async () => {
            try {
                const response = await axios.get('/api/tenant/config');
                if (cancelled) return;
                if (response.data.success && response.data.data) {
                    setHostnameTenant(response.data.data);
                } else {
                    setHostnameTenant(null);
                }
            } catch (err) {
                console.error('Failed to fetch tenant config', err);
                if (!cancelled) setError('Failed to load configuration');
            } finally {
                if (!cancelled) setHostnameLoading(false);
            }
        };

        fetchHostnameTenant();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (hostnameTenant || !shadowSlug) {
            setShadowTenant(null);
            setShadowLoading(false);
            return;
        }

        let cancelled = false;
        setShadowLoading(true);

        (async () => {
            try {
                const response = await axios.get(`/api/tenant/config/by-slug/${encodeURIComponent(shadowSlug)}`);
                if (cancelled) return;
                if (response.data.success) {
                    setShadowTenant(response.data.data);
                    setError(null);
                } else {
                    setShadowTenant(null);
                }
            } catch (err) {
                console.error('Failed to fetch shadow tenant config', err);
                if (!cancelled) {
                    setShadowTenant(null);
                    setError('Failed to load organization');
                }
            } finally {
                if (!cancelled) setShadowLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [hostnameTenant, shadowSlug]);

    const tenantPath = useCallback(
        (path: string) => prefixTenantPath(path, shadowPrefix),
        [shadowPrefix]
    );

    const stripTenantPrefix = useCallback(
        (pathname: string) => stripShadowPrefix(pathname, shadowPrefix),
        [shadowPrefix]
    );

    /**
     * The club's colour, published as a custom property on :root so every part
     * of the tenant site can reach it — including page content stored as HTML,
     * which has no other way to know what the club's colour is.
     */
    const accent = tenant?.branding?.primaryColor || tenant?.branding?.accentColor || null;
    useEffect(() => {
        const root = document.documentElement;
        if (accent) root.style.setProperty('--tenant-accent', accent);
        else root.style.removeProperty('--tenant-accent');
        return () => { root.style.removeProperty('--tenant-accent'); };
    }, [accent]);

    /**
     * How tall this club's header bar is, published on :root so the sticky
     * sub-headers that sit under it stay flush when a club runs a bigger one.
     */
    const headerHeight = headerHeightFor(tenant);
    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--app-header-h', `${headerHeight}px`);
        return () => { root.style.removeProperty('--app-header-h'); };
    }, [headerHeight]);

    /**
     * An editorial club sets its typeface for the whole site, not just the
     * furniture. A body class is the only practical lever: Tailwind's
     * `font-sans` is applied all over the app and would otherwise win over any
     * inheritance from body (see the rule in index.css).
     */
    const editorial = String(tenant?.branding?.theme || '').toLowerCase() === 'editorial';
    useEffect(() => {
        document.body.classList.toggle('theme-editorial', editorial);
        return () => { document.body.classList.remove('theme-editorial'); };
    }, [editorial]);

    const value = {
        tenant,
        loading,
        error,
        isTenant: !!tenant,
        isShadowTenant: !!shadowPrefix && !!tenant,
        shadowPrefix,
        accent,
        editorial,
        tenantPath,
        stripTenantPrefix,
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export default TenantContext;
